<script lang="ts">
	import { get } from 'svelte/store';
	import { onMount } from 'svelte';
	//Formating
	import { format } from 'svelte-i18n';
	// Design system components
	import {
		CardGrid,
		DashBarChartWidget,
		IconButton,
		LabelList,
		LabelListV2,
		Loader,
		Text,
		Tooltip,
	} from '@Shelly/shelly-design-system';
	// Helpers
	import {
		addDate,
		dateFormat,
		getTimeZoneOffset,
		getUserTimeZoneDate,
	} from '../../helpers/date_helper';
	import { capFirstChar } from 'src/helpers/utilities';
	import { parseStatisticsData } from 'src/helpers/statistics/core/parse_statistics_data';
	import { getOverAllChartsConfig } from 'src/helpers/statistics/config/overall_consumption';
	import { addLastHourConsumption } from 'src/helpers/statistics/core/last_hour_helpers/last_hour_consumption_helpers';
	import { generateCSVStatistic } from 'src/helpers/statistics/ui/chart_download_helpers';
	import { wait } from '../../helpers/utilities';
	// Stores
	import { rooms } from 'src/stores/lists/rooms';
	// Components
	// import LayerChart from '../Chart/LayerChart/charts/LayerChart.svelte';
	import LayerChartRenderer from '../Chart/LayerChart/LayerChartRenderer.svelte';
	import CacheStatisticRequest from 'src/helpers/statistics/request/chart_cache_statistics_helpers';
	import ConsumptionContainer from './ConsumptionContainer.svelte';
	import ConsumptionOverallCard from './ConsumptionOverallCard.svelte';
	import ConsumptionOverallChart from './ConsumptionOverallChart.svelte';
	import LazyLoad from '../LazyLoad.svelte';
	import { prepareTotalEnergyDashChartData } from 'src/helpers/statistics/ui/dash_chart_widget_helpers';
	// Types
	import type { ChartConfigInterface, ChartDataInterface } from '../../types/Chart';
	import { type ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
	import type {
		GetStatsPeriodRequest,
		GetStatsResponseData,
		RequestStatisticData,
	} from '../../types/requests/charts/get_stats_period';

	import VerivoxSuggestion from 'src/components/PromotionalIntegrations/Verivox/components/suggestion/VerivoxSuggestion.svelte';
	import { isEnergyPriceFeatureEnabled } from 'src/helpers/energy_price_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { mapIconSolidWeightToLight, mapLegacyColorToToken } from 'src/helpers/new-layout-helpers';

	export let chartConfig: ChartConfigInterface,
		reqURL: string,
		title: string,
		roomID: number = -1,
		chartControlsData: ChartControlsDataInterface,
		index = 0,
		hideTooltip = false,
		dashWidget = false;

	let loadedTotalRequest = false,
		loaded = false;

	const getStatsPeriod = async (
		data: ChartControlsDataInterface,
		dummyNetMetering = false,
	): Promise<ChartDataInterface> => {
		if (!data) return;
		loadedTotalRequest = false;
		if (window.innerHeight >= 1080) await wait(500 * index);
		const userTimeZoneDate = getUserTimeZoneDate();
		// date_range with property "custom" doesn't work correctly with
		// time intervals of less than 24 hours and greater than 1 hour
		const from = data.time.from,
			to = data.time.to,
			oneHour = 60 * 60 * 1000,
			timeDiff = to.getTime() - from.getTime(),
			dailyRange = timeDiff > oneHour && timeDiff <= oneHour * 24,
			reqData: RequestStatisticData = {
				room_id: roomID > -1 ? roomID : undefined,
				date_range: dailyRange ? 'day' : 'custom',
				date_from: dateFormat(from, data.format),
				date_to: dailyRange ? undefined : dateFormat(to, data.format),
			},
			lastHour = userTimeZoneDate.date;
		lastHour.setMinutes(0, 0, 0);
		const res: GetStatsPeriodRequest = await CacheStatisticRequest.getData(
			reqURL,
			reqData,
			undefined,
		);
		if (!res?.error && res?.history) {
			loadedTotalRequest = true;
			let resData: GetStatsResponseData = res?.history ? (res as GetStatsResponseData) : res.data;
			if (
				!/^hour$/.test(data.rangeOption.period) &&
				data.time.to.getTime() >= lastHour.getTime()
			) {
				const lastHourRes = await CacheStatisticRequest.getData(
					reqURL,
					{
						...reqData,
						date_range: 'custom',
						date_from: dateFormat(lastHour, data.format),
						date_to: dateFormat(addDate(lastHour, 'h'), data.format),
					},
					undefined,
				);
				if (!lastHourRes?.error && lastHourRes?.history) {
					resData = addLastHourConsumption(
						resData,
						lastHourRes?.history ? (lastHourRes as GetStatsResponseData) : lastHourRes.data,
						data,
					);
				}
			}
			const result = parseStatisticsData(
				resData,
				chartConfig.charts[0],
				data.rangeOption.period,
				null,
				null,
				dummyNetMetering,
			);
			/*if (
				roomID != -1 &&
				loadedTotalRequest &&
				result?.totalConsumption?.length === 0
			) {
				hideRoomWithoutConsumption = true;
			}*/
			if (userTimeZoneDate.tz != res.timezone) {
				console.log(
					"The user's timezone and the timezone of the request result are different.",
					{ userTimeZoneDate },
					{ res },
				);
			}
			return result;
		} else {
			throw new Error(
				$format('failed_to_get_statistics_data', {
					default: 'Failed to get statistics data',
				}),
			);
		}
	};

	const getRooms = (): any => {
		const allRooms = get(rooms);
		// const roomsWithPM = Object.values(get(devices))
		// 	.filter(
		// 		(device: Device) =>
		// 			devices_config[device.type]?.isPM &&
		// 			!device.shared &&
		// 			device.room_id >= 0
		// 	)
		// 	.reduce((result, device: Device) => {
		// 		const roomID = device.room_id;
		// 		if (!result[roomID]) result[roomID] = allRooms[roomID];
		// 		return result;
		// 	}, {});
		return Object.values(allRooms).sort((a: any, b: any) => {
			return a.position - b.position;
		});
	};

	let dummyNetMetering = false;

	$: dashInfoText = capFirstChar(
		$format('option_24_hours', {
			default: 'Last 24 hours',
		}),
	);

	onMount(() => {
		if (dashWidget) loaded = true;
	});

	$: statsPromise =
		loaded && chartControlsData ? getStatsPeriod(chartControlsData, dummyNetMetering) : null;

	$: statsData = statsPromise;
</script>

{#if dashWidget}
	{#if !statsPromise}
		<DashBarChartWidget
			aria-label="Dash Chart Overall Consumption Widget"
			{title}
			infoText={dashInfoText}
			icon="fas fa-bolt"
			iconColor="--color-primary"
		>
			<div class="d-flex justify-content-center align-items-center" style="height: 220px">
				<Loader size="m" />
			</div>
		</DashBarChartWidget>
	{:else}
		{#await statsPromise}
			<DashBarChartWidget
				aria-label="Dash Chart Overall Consumption Widget"
				{title}
				infoText={dashInfoText}
				icon="fas fa-bolt"
				iconColor="--color-primary"
			>
				<div class="d-flex justify-content-center align-items-center" style="height: 220px">
					<Loader size="m" />
				</div>
			</DashBarChartWidget>
		{:then chartData}
			<div class:pointer-events-none={hideTooltip}>
				<DashBarChartWidget
					aria-label="Dash Chart Overall Consumption Widget"
					{title}
					infoText={dashInfoText}
					icon="fas fa-bolt"
					iconColor="--color-primary"
					chartData={chartData ? prepareTotalEnergyDashChartData(chartData) : undefined}
				>
					{#if !chartData}
						<div
							class="d-flex justify-content-center align-items-center"
							style="height: 220px"
						>
							<Text class="body-small-styles text-center text-color-muted">
								{$format('no_data_available', { default: 'No data available' })}
							</Text>
						</div>
					{/if}
				</DashBarChartWidget>
			</div>
		{:catch error}
			<DashBarChartWidget
				aria-label="Dash Chart Overall Consumption Widget"
				{title}
				infoText={dashInfoText}
				icon="fas fa-bolt"
				iconColor="--color-primary"
			>
				<div class="d-flex justify-content-center align-items-center" style="height: 220px">
					<Text class="body-small-styles text-center mt-m mb-s ">
						<i class="fas fa-exclamation-triangle text-warning mr-xs" />
						{error.message}
					</Text>
				</div>
			</DashBarChartWidget>
		{/await}
	{/if}
{:else}
	<ConsumptionContainer>
		<LazyLoad
			minHeight={330}
			on:load={() => {
				loaded = true;
			}}
		>
			{#await statsData}
				{#if title}
					<div class="mb-s d-flex align-items-center">
						<Text class="text-bold width-full" style="margin-right:-32px">
							{`${title} - ${$format(`option_${chartControlsData.currentPeriod}`)}`}
						</Text>
						<IconButton
							aria-label={$format('generate_csv_file', {
								default: 'Generate CSV file',
							})}
							variant="secondary"
							icon="far fa-file-arrow-down"
							disabled
						/>
					</div>
				{/if}
				<div
					class="d-flex justify-content-center align-items-center"
					style={`height: ${chartConfig.charts[0].height}px`}
				>
					<Loader size="m" />
				</div>
			{:then chartData}
				{#if chartData}
					{#if title}
						<div class="mb-s d-flex align-items-center justify-content-between">
							<Text class="text-bold width-full" noWrap style="text-indent:32px">
								{`${title} - ${$format(`option_${chartControlsData.currentPeriod}`)}`}
							</Text>
							{#if dummyNetMetering || (chartData?.totalReversedConsumption && chartData?.totalRoomConsuption)}
								<Tooltip
									placement="top"
									class="mr-s"
									title={$format('net_metering_tab', {
										default: 'Net Metering',
									})}
								>
									<IconButton
										size="m"
										aria-label={$format('net_metering_tab', {
											default: 'Net Metering',
										})}
										variant={dummyNetMetering ? 'success' : 'primary'}
										icon="fas fa-solar-panel"
										on:click={() => {
											// if (!dummyNetMeteringPopUp) {
											//  dummyNetMeteringPopUp = true;
											// 	showNetMeteringModal = true;
											// 	return;
											// }
											dummyNetMetering = !dummyNetMetering;
										}}
									/>
								</Tooltip>
							{/if}
							<IconButton
								variant="secondary"
								icon="far fa-file-arrow-down"
								aria-label={$format('generate_csv_file', {
									default: 'Generate CSV file',
								})}
								on:click={() => {
									generateCSVStatistic(chartData, title, chartControlsData.time);
								}}
							/>
						</div>
					{/if}
					<div class="d-flex justify-content-center mb-s">
						{#if $showNewLayout}
							<LabelListV2
								size="m"
								labelList={chartData.totalConsumption
									.concat(chartData?.totalReturnedConsumption || [])
									.map((el) => ({
										...el,
										icon: mapIconSolidWeightToLight(el?.icon),
										//@ts-ignore
										backgroundColor: mapLegacyColorToToken(el?.type),
									})) || []}
								a11yProps={{ isInteractive: true }}
							/>
						{:else}
							<LabelList
								size="xl"
								labelList={chartData.totalConsumption.concat(
									chartData?.totalReturnedConsumption || [],
								)}
							/>
						{/if}
					</div>
					<!-- Alex: was thinking of adding a second row label for te returned total... leaving it here to show as an option -->
					<!-- <div class="d-flex justify-content-center">
					<LabelList size="m" labelList={chartData.totalReturnedConsumption} />
				</div> -->
					<div class:pointer-events-none={hideTooltip}>
						<LayerChartRenderer {chartData} />
					</div>

					{#if roomID >= 0}
						<ConsumptionOverallCard
							{roomID}
							totalRoomConsuption={chartData.totalRoomConsuption}
							totalReversedConsumption={chartData.totalReversedConsumption}
							roomDevices={chartData.devices}
							units={chartData.units}
							{dummyNetMetering}
						/>
					{:else}
						<Text size="xs" class="mt-s tiny-styles text-secondary">
							{capFirstChar(
								$format('local_time_zone_user_account', {
									default: `local time-zone of the user account`,
								}),
							)}: {chartData.timezone}
							{@const timeZoneOffset = getTimeZoneOffset(chartData.timezone)}
							{#if timeZoneOffset != 0}
								{`${getUserTimeZoneDate().time} (${timeZoneOffset > 0 ? '+' : ''}${timeZoneOffset}${$format(
									'local_time_zone_from_current_time_zone',
									{
										default: 'h from current time zone',
									},
								)})`}
							{/if}
						</Text>
					{/if}
				{/if}
			{:catch error}
				<div
					class="d-flex justify-content-center align-items-center"
					style={`height: ${chartConfig.charts[0].height}px`}
				>
					<Text class="body-small-styles text-center mt-m mb-s ">
						<i class="fas fa-exclamation-triangle text-warning mr-xs" />
						{error.message}
					</Text>
				</div>
			{/await}
		</LazyLoad>
	</ConsumptionContainer>

	{#if isEnergyPriceFeatureEnabled() && roomID == -1}
		<VerivoxSuggestion {chartControlsData} />
	{/if}

	{#if roomID == -1 && loadedTotalRequest}
		<CardGrid lg={380} sm={270} class="mt-md-m mt-sm-s">
			{@const rooms = getRooms()}
			{@const chartConfig = getOverAllChartsConfig('area')}
			{#each rooms as room, i}
				<ConsumptionOverallChart
					{chartConfig}
					{chartControlsData}
					reqURL={chartConfig.url}
					title={room.name}
					roomID={room.id}
					index={i}
				/>
			{/each}
		</CardGrid>
	{/if}
{/if}
